const { Observable, BehaviorSubject } = require('rxjs');
const { PENDING } = require('../../muster-utils');
const fromPairs = require('lodash/fromPairs');

const RefCounter = require('./utils/ref-counter');

module.exports = class Store {
  constructor({
    log,
  } = {}) {
    this.log = log;
    this.outputStream = new BehaviorSubject([]).share();
    this.refCounter = new RefCounter();
    this.values = new Map();
  }

  has(paths) {
    paths.forEach((path) => { this.log.debug('HAS', path); });
    return paths.every((path) => this.values.has(path));
  }

  get(paths) {
    paths.forEach((path) => { this.log.debug('RETRIEVE', path); });
    return paths.map((path) => this.values.get(path));
  }

  set(pathValues) {
    pathValues.forEach(({ path, value }) => { this.log.debug('UPDATE', path, value); });

    // TODO: check if retain count > 0, warn if not

    const updatedPathValues = pathValues.filter(
      ({ path, value }) => !this.values.has(path) || (this.values.get(path) !== value)
    );
    updatedPathValues.forEach(({ path, value }) => {
      this.log.debug('EMIT', path, value);
      this.values.set(path, value);
    });

    if (updatedPathValues.length > 0) { this.outputStream.next(updatedPathValues); }
  }

  delete(paths) {
    paths.forEach((path) => {
      this.log.debug('DELETE', path);
      this.values.delete(path);
    });
  }

  retain(paths) {
    paths.forEach((path) => {
      const refCount = this.refCounter.increment(path);
      this.log.debug('RETAIN', path, refCount);
    });
  }

  release(paths) {
    const unreferencedPaths = paths.filter((path) => {
      const refCount = this.refCounter.decrement(path);
      this.log.debug('RELEASE', path, refCount);
      return (refCount === 0);
    });
    this.delete(unreferencedPaths);
  }

  listen(paths, { keepAlive = 0 } = {}) {
    this.log.debug('SUBSCRIBE', paths);

    // Create a lookup table to quickly filter incoming path updates
    const pathsMap = fromPairs(paths.map((path) => [path, true]));

    // Increase the retain count as soon as a subscription is opened
    return Observable.empty().do({ complete: () => this.retain(paths) })

      // Add an initial delay to ensure values are returned asynchronously
      .concat(Observable.fromPromise(Promise.resolve(true)))

      // Get the initial path values
      .map(() => getInitialPathValues(paths, this))
      .do((initialValue) => this.log.debug('INITIAL', initialValue))

      // Now that we have a full set of initial values, we can emit this immediately and output a
      // stream that emits any future updates as they come into the store
      .switchMap((initialValue) =>

        // Create a filtered stream that feeds off the main output stream
        this.outputStream
          // Filter out unwatched paths
          .map((pathValues) => pathValues.filter(({ path }) => path in pathsMap))
          // Don't emit an update if there are no relevant path updates
          .filter((pathValues) => pathValues.length > 0)

          // Ensure that the initial value is emitted before any updates
          .startWith(initialValue)
      )

      // Release cached values when the subscription is disposed
      .finally(() => {
        this.log.debug('UNSUBSCRIBE', paths);
        // Optionally delay garbage collection
        maybeDelay(keepAlive, () => this.release(paths));
      });
  }
};

function getInitialPathValues(paths, store) {
  return paths.map((path) => (store.has([path]) ?
    { path, value: store.get([path])[0] } : { path, value: PENDING }
  ));
}

function maybeDelay(timeout, callback) { // eslint-disable-line consistent-return
  if (timeout) { return setTimeout(callback, timeout); }
  return Promise.resolve().then(callback);
}
